import { appendAppLog } from './appLog';

/**
 * Ungefangene Fehler und ungefangene Ablehnungen festhalten.
 *
 * ⛔ **Bis zum 2026-09-25 kamen sie nirgends an.** Im Dateilog steht nur, was jemand
 * ausdrücklich hineinschreibt — und in Xcode steht ein `unhandledrejection` aus dem WKWebView
 * nicht verlässlich, weil er die native Seite gar nicht erreicht. Ein Fehler ohne jede Spur
 * ist am Prüfungstag nicht zu finden; genau daran hat der Befund vom 2026-09-24 einen Tag
 * verloren.
 *
 * Seit `appendAppLog` jede Zeile auch auf die Konsole gibt, landet von hier aus beides an
 * beiden Orten: in der Datei für den Prüfungstag, am Kabel für die Werkstatt.
 *
 * ⚠️ **Das ersetzt keinen `try`/`catch`.** Was hier ankommt, ist bereits durchgefallen — die
 * Stelle, an der es entstand, hat es nicht behandelt. Das Netz fängt es auf, damit man es
 * *sieht*, nicht damit man es liegen lassen kann.
 */

/**
 * Nur das, was gebraucht wird — nicht `Window`. Der Prüflauf hat kein Fenster
 * (`vitest.config.ts` läuft mit `environment: 'node'`), und was nicht ohne Gerät prüfbar ist,
 * bleibt kaputt.
 */
export interface AbsturzZiel {
  addEventListener(typ: string, fn: (ereignis: unknown) => void): void;
  removeEventListener(typ: string, fn: (ereignis: unknown) => void): void;
}

/** Ein Feld eines Ereignisses, ohne über seine Form etwas zu behaupten. */
function feld(ereignis: unknown, name: string): unknown {
  if (typeof ereignis !== 'object' || ereignis === null) return undefined;
  return (ereignis as Record<string, unknown>)[name];
}

/** Ein Wert als Text — Fehler mit ihrem Stapel, alles andere so gut es geht. */
function alsText(wert: unknown): string {
  if (wert === undefined || wert === null) return '';
  if (wert instanceof Error) return wert.stack ?? wert.message;
  const stapel = feld(wert, 'stack');
  if (typeof stapel === 'string' && stapel !== '') return stapel;
  try {
    return typeof wert === 'object' ? (JSON.stringify(wert) ?? String(wert)) : String(wert);
  } catch {
    // Ringschluss oder ein Wert, den `JSON` nicht mag.
    return String(wert);
  }
}

/** `datei:zeile:spalte`, soweit das Ereignis es hergibt. Leer, wenn es nichts nennt. */
function stelle(ereignis: unknown): string {
  const datei = feld(ereignis, 'filename');
  if (typeof datei !== 'string' || datei === '') return '';
  const zeile = feld(ereignis, 'lineno');
  const spalte = feld(ereignis, 'colno');
  return ' (' + datei + (typeof zeile === 'number' ? ':' + String(zeile) : '') +
    (typeof spalte === 'number' ? ':' + String(spalte) : '') + ')';
}

/**
 * Beide Horcher anmelden. Gibt die Funktion zum Abmelden zurück.
 *
 * Gehört ganz nach vorn in `main.tsx`, direkt hinter `starteKonsolenMitschrift()` — was davor
 * schiefgeht, ist nicht mitzubekommen.
 *
 * `log` ist einschiebbar, damit der Ablauf ohne Dateisystem prüfbar bleibt.
 */
export function fangeAbstuerze(
  ziel: AbsturzZiel,
  log: (text: string, stufe: 'error') => unknown = appendAppLog,
): () => void {
  /**
   * ⚠️ **Kein Horcher darf werfen.** Ein geworfener Fehler in einem `error`-Horcher wird
   * selbst wieder zum ungefangenen Fehler und landet hier erneut — eine Schleife, die der
   * Prüfungstag nicht übersteht. Deshalb wird jede Ausgabe eingepackt, und eine abgelehnte
   * Zusage aus `log` wird ausdrücklich abgefangen statt bloss ignoriert.
   */
  const schreibe = (text: string): void => {
    try {
      const ergebnis = log(text, 'error');
      if (ergebnis instanceof Promise) void ergebnis.catch(() => undefined);
    } catch {
      // Hier endet es. Mehr lässt sich nicht tun, ohne wiederum vom Protokoll abzuhängen.
    }
  };

  const beiFehler = (ereignis: unknown): void => {
    const nachricht = feld(ereignis, 'message');
    const ursache = alsText(feld(ereignis, 'error'));
    schreibe(
      'Ungefangener Fehler: ' + (typeof nachricht === 'string' && nachricht !== '' ? nachricht : '(ohne Angabe)') +
        stelle(ereignis) + (ursache !== '' ? ' — ' + ursache : ''),
    );
  };

  const beiAblehnung = (ereignis: unknown): void => {
    const grund = alsText(feld(ereignis, 'reason'));
    schreibe('Ungefangene Ablehnung: ' + (grund !== '' ? grund : '(ohne Angabe)'));
  };

  ziel.addEventListener('error', beiFehler);
  ziel.addEventListener('unhandledrejection', beiAblehnung);

  return () => {
    ziel.removeEventListener('error', beiFehler);
    ziel.removeEventListener('unhandledrejection', beiAblehnung);
  };
}
